import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowRight, User, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { acceptInvite } from '../../services/user-invite';
import { AuthLayoutCentered } from '../../components/auth-layout';
import ghostIcon from '../../assets/ghost.png';

const INPUT_CLASS =
  'h-11 w-full rounded-xl pl-11 pr-4 text-sm outline-none transition-all duration-200 placeholder:text-white/20 focus:ring-2 focus:ring-[#AD34FE]/40';
const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#f5f0fa',
};
const CARD_STYLE = {
  background: 'rgba(26, 14, 36, 0.7)',
  border: '1px solid rgba(173, 52, 254, 0.15)',
  backdropFilter: 'blur(32px)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset',
};
const ICON_WRAP = 'absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none';
const LABEL_CLASS = 'text-xs font-medium uppercase tracking-wider';
const LABEL_STYLE = { color: 'rgba(255,255,255,0.4)' };

function PasswordField({ id, name, placeholder, minLength }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <div className={ICON_WRAP} style={{ color: 'rgba(255,255,255,0.3)' }}>
        <Lock className="h-4 w-4" />
      </div>
      <input
        id={id}
        name={name}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        required
        minLength={minLength}
        autoComplete="new-password"
        className={`${INPUT_CLASS} pr-11`}
        style={INPUT_STYLE}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center transition-colors"
        style={{ color: 'rgba(255,255,255,0.3)', background: 'transparent', border: 'none' }}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    const { name, password, confirmPassword } = Object.fromEntries(new FormData(e.target));
    if (password !== confirmPassword) {
      toast.error('As senhas não conferem!');
      return;
    }
    setIsLoading(true);
    try {
      await acceptInvite(token, name, password);
      setSuccess(true);
      toast.success('Conta criada com sucesso!');
    } catch (err) {
      toast.error(err?.data?.message || 'Falha ao aceitar convite. O link pode estar expirado.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthLayoutCentered>
      <div className="flex flex-col gap-8">
        {/* Brand */}
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="animate-ghost-float relative">
            <div
              className="animate-ghost-glow absolute inset-0 rounded-full blur-2xl"
              style={{ background: '#AD34FE' }}
            />
            <img
              src={ghostIcon}
              alt="Verboo"
              className="relative h-[72px] w-[72px] object-contain drop-shadow-lg"
            />
          </div>
          <div>
            <h1 className="text-xl font-semibold" style={{ color: '#fff' }}>
              {success ? 'Conta criada!' : 'Criar sua conta'}
            </h1>
            <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {success
                ? 'Você já pode acessar a plataforma com seu email e senha.'
                : 'Você foi convidado para a Verboo! Complete seu cadastro.'}
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-6 flex flex-col gap-5" style={CARD_STYLE}>
          {success ? (
            <div className="flex flex-col items-center text-center py-4 gap-4">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{ background: 'rgba(173,52,254,0.1)', border: '1px solid rgba(173,52,254,0.2)' }}
              >
                <CheckCircle2 className="h-6 w-6" style={{ color: '#AD34FE' }} />
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Faça login com seu email e a senha que você acabou de criar.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-2 h-11 w-full rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98]"
                style={{
                  color: '#fff',
                  background: 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)',
                  boxShadow: '0 0 32px rgba(173,52,254,0.4), 0 4px 16px rgba(0,0,0,0.3)',
                  border: 'none',
                }}
              >
                Ir para Login
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className={LABEL_CLASS} style={LABEL_STYLE}>Nome</label>
                <div className="relative">
                  <div className={ICON_WRAP} style={{ color: 'rgba(255,255,255,0.3)' }}>
                    <User className="h-4 w-4" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Seu nome"
                    required
                    autoComplete="name"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className={LABEL_CLASS} style={LABEL_STYLE}>Criar Senha</label>
                <PasswordField id="password" name="password" placeholder="••••••••" minLength={6} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="confirmPassword" className={LABEL_CLASS} style={LABEL_STYLE}>Confirmar Senha</label>
                <PasswordField id="confirmPassword" name="confirmPassword" placeholder="••••••••" />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="mt-1 h-11 w-full rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  color: '#fff',
                  background: isLoading
                    ? 'rgba(173,52,254,0.4)'
                    : 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)',
                  boxShadow: isLoading
                    ? 'none'
                    : '0 0 32px rgba(173,52,254,0.4), 0 4px 16px rgba(0,0,0,0.3)',
                  border: 'none',
                }}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <span>Criar Conta</span>
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </AuthLayoutCentered>
  );
}
